// Phone lines (dentalcrm CU-869fkj67y, contract ~/pm/contracts/B3.md v1) and
// desks (CU-869fm5hhp, contract B5.md v1) — the ONLY place that knows those
// routes and row shapes. Like voiceConnections.api.js: if the backend lands
// with a different path or field name, change it here and in the tests.
//
// Normalisers are tolerant on purpose: a missing count reads as 0, never NaN.
import { fVoiceDateTime, voiceConnectionsBase } from './voiceConnections.api';
import { parseApiDate } from '../../callLogs/callLog.normalize';

export { fVoiceDateTime };

export const LINE_URLS = {
    // Numbers taking calls that are not on the connection's list (admin).
    unregistered: (practiceId, connectionId, days = 30) => `${voiceConnectionsBase(practiceId)}${connectionId}/unregistered-lines/?days=${days}`,
    // Bulk set up, max 50 per request (admin). The recording backfill always runs on a real save.
    setUp: (practiceId, connectionId) => `${voiceConnectionsBase(practiceId)}${connectionId}/lines/`,
    // Any Call Logs viewer, for one call on a line that is not set up.
    askAdmin: (practiceId, callId) => `/api/v1/practices/${practiceId}/voice-calls/${callId}/ask-admin/`,
};

export const DESK_URLS = {
    list: (practiceId, connectionId) => `${voiceConnectionsBase(practiceId)}${connectionId}/desks/`,
    sync: (practiceId, connectionId) => `${voiceConnectionsBase(practiceId)}${connectionId}/desks/sync/`,
    // Desk claims for admins (dentalcrm CU-869fmd1vf). POST → the desk row.
    release: (practiceId, connectionId, deskId) => `${voiceConnectionsBase(practiceId)}${connectionId}/desks/${deskId}/release/`,
    // POST { practice_user_id } → the desk row | 400 { practice_user_id } | 409 { code, detail }
    assign: (practiceId, connectionId, deskId) => `${voiceConnectionsBase(practiceId)}${connectionId}/desks/${deskId}/assign/`,
    // GET ?date=YYYY-MM-DD (UK day, default today) → { desk_id, date, claims: [...] }
    history: (practiceId, connectionId, deskId) => `${voiceConnectionsBase(practiceId)}${connectionId}/desks/${deskId}/history/`,
    // PUT { practice_user_ids } → the desk row (whose usual desk it is; one each)
    defaults: (practiceId, connectionId, deskId) => `${voiceConnectionsBase(practiceId)}${connectionId}/desks/${deskId}/defaults/`,
    // PUT { practice_user_ids } → the desk row (who shares this handset; many each)
    share: (practiceId, connectionId, deskId) => `${voiceConnectionsBase(practiceId)}${connectionId}/desks/${deskId}/share/`,
};

export const SET_UP_BATCH = 50;
const count = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export const normaliseUnregisteredLine = (row, connectionId) => ({
    number: String(row?.number || ''),
    connectionId,
    // `held` = shared account: calls were received but not saved until the line is set up.
    held: row?.status === 'held',
    calls: count(row?.calls),
    heldCalls: count(row?.held_calls),
    lastSeenAt: row?.last_seen_at || null,
    ownedByAnotherPractice: Boolean(row?.owned_by_another_practice),
    adminAlertedAt: row?.admin_alerted_at || null,
});

// Numbers registered to another practice are refused on set-up, so they never
// count towards "N numbers aren't set up".
export const settableLines = (lines) => lines.filter((l) => !l.ownedByAnotherPractice);

// A number that IS set up: the half the screen never showed, so nobody could tell
// what "set up" bought them.
export const normaliseRegisteredLine = (row, connectionId) => ({
    number: String(row?.number || ''),
    connectionId,
    name: String(row?.name || ''),
    site: String(row?.site || ''),
    calls: count(row?.calls),
    inboundCalls: count(row?.inbound_calls),
    outboundCalls: count(row?.outbound_calls),
    lastSeenAt: row?.last_seen_at || null,
});

export const normaliseUnregistered = (data, connectionId) => ({
    lines: (Array.isArray(data?.lines) ? data.lines : []).map((r) => normaliseUnregisteredLine(r, connectionId)).filter((l) => l.number),
    registered: (Array.isArray(data?.registered) ? data.registered : []).map((r) => normaliseRegisteredLine(r, connectionId)).filter((l) => l.number),
    // Numbers on this phone account that belong to another practice. Counted, never
    // named: sharing an account is not permission to see their inventory.
    ownedElsewhere: count(data?.owned_elsewhere),
    sharedAccount: Boolean(data?.shared_account),
});

// Rows grouped per connection, chunked to the backend's 50-line cap.
export const setUpRequests = (rows) => {
    const byConnection = rows.reduce((acc, r) => {
        (acc[r.connectionId] = acc[r.connectionId] || []).push({ number: r.number, name: String(r.name || '').trim(), site: String(r.site || '').trim() });
        return acc;
    }, {});
    return Object.entries(byConnection).flatMap(([connectionId, lines]) => {
        const chunks = [];
        for (let i = 0; i < lines.length; i += SET_UP_BATCH) chunks.push({ connectionId, body: { lines: lines.slice(i, i + SET_UP_BATCH), dry_run: false } });
        return chunks;
    });
};

const BACKFILL_KEYS = ['calls_on_these_lines', 'recordings_to_fetch', 'older_than_30_days', 'held_calls_to_release'];
export const sumBackfill = (responses) => BACKFILL_KEYS.reduce((acc, key) => {
    acc[key] = responses.reduce((n, r) => n + count(r?.backfill?.[key]), 0);
    return acc;
}, {});

// First readable message from a 400: {"detail": …} or {"lines": [{"number": ["…"]}]}.
export const setUpError = (data) => {
    if (data?.detail) return String(data.detail);
    const perLine = Array.isArray(data?.lines) ? data.lines.flatMap((l) => Object.values(l || {}).flat()) : [];
    return perLine.find((m) => typeof m === 'string') || null;
};

// Desk status vocabulary (B5 read side; B6 fills connection_state).
export const DESK_STATUS = {
    not_connected: { label: 'Not connected', color: 'default' },
    connected: { label: 'Connected', color: 'success' },
    needs_reconnecting: { label: 'Needs reconnecting', color: 'warning' },
};
export const deskStatusMeta = (status) => DESK_STATUS[status] || DESK_STATUS.not_connected;

export const DESK_SYNC_ERRORS = {
    address_book_scope_missing: 'The Elevate app needs address book access (api.service.address-book) before handsets can be listed.',
    auth: 'Elevate refused the stored credentials. Reconnect the phone system with new ones.',
    rate_limited: 'Elevate is limiting requests. Handsets will sync again automatically.',
    too_many_desks: 'This Elevate account has more handsets than the CRM can list (500).',
};
export const deskSyncErrorText = (code) => (code ? DESK_SYNC_ERRORS[code] || 'Handsets could not be read from Elevate. They sync again every morning; try Sync now later.' : '');

// Recording status per desk (CU-869fmc1yk): checked every 15 minutes from the
// desk's calls. Older backends send nothing, which reads as unknown.
export const DESK_RECORDING = {
    on: { label: 'On', color: 'success' },
    stopped: { label: 'Stopped', color: 'error' },
    ring_group: { label: 'Not recorded – ring group', color: 'default' },
    unknown: { label: 'No recent calls', color: 'default' },
};
export const deskRecordingLabel = (desk) => (desk.recording === 'stopped' && desk.recordingStoppedSince
    ? `Stopped since ${fVoiceDateTime(desk.recordingStoppedSince)}`
    : (DESK_RECORDING[desk.recording] || DESK_RECORDING.unknown).label);

const deskPerson = (d) => ({ practiceUserId: d.practice_user_id, name: d.name || '', canViewCallLogs: d.can_view_call_logs !== false });

export const normaliseDesk = (row) => ({
    id: String(row?.id ?? ''),
    name: row?.name || row?.extension || row?.email || 'Unnamed handset',
    extension: row?.extension || '',
    isActive: row?.is_active !== false,
    teamMember: row?.practice_user_name || '',
    // Who this handset is for, and the keys the link is written under: the row's own
    // id is a database pk, while user_mapping is matched on the Elevate user id.
    practiceUserId: row?.practice_user_id ?? null,
    unifiedUserId: row?.unified_user_id ? String(row.unified_user_id) : '',
    mappingVendorUserId: row?.mapping_vendor_user_id || '',
    email: row?.email || '',
    status: DESK_STATUS[row?.connection_state] ? row.connection_state : 'not_connected',
    recording: DESK_RECORDING[row?.recording_status] ? row.recording_status : 'unknown',
    recordingStoppedSince: row?.recording_stopped_since || null,
    recordingCallsSince: count(row?.recording_calls_since),
    // Who is at the desk now (CU-869fmd1vf); null = nobody (or an older backend).
    holder: row?.holder ? {
        name: row.holder.name || 'Someone',
        practiceUserId: row.holder.practice_user_id ?? null,
        staffSupport: row.holder.is_staff_support === true,
        since: row.holder.since || null,
        expiresAt: row.holder.expires_at || null,
    } : null,
    // Team members whose usual desk this is.
    // Seen answering for this practice: the only evidence the address book gives that a
    // handset is ours rather than another practice's on the same Elevate account.
    ours: row?.ours !== false,
    // Everyone who shares this handset: they all answer for it, nobody signs in to it.
    // canViewCallLogs false: their role can't see Call Logs, so they are never told about
    // this handset's calls (CU-869fn2wn5). An older backend does not say: assume yes.
    sharedWith: Array.isArray(row?.shared_with) ? row.shared_with.filter((d) => d && d.practice_user_id != null).map(deskPerson) : [],
    defaultFor: Array.isArray(row?.default_for) ? row.default_for.filter((d) => d && d.practice_user_id != null).map(deskPerson) : [],
});

// How a claim on a desk ended, for the desk's "Today" history (CU-869fmd1vf).
export const DESK_CLAIM_ENDED = {
    still_here: 'Still at the handset',
    released: 'Released',
    logout: 'Signed out',
    taken_over: 'Taken over',
    assigned: 'Moved by an admin',
    released_by_admin: 'Released by an admin',
    moved: 'Moved to another handset',
    expired: 'Expired after 12 hours',
    unknown: 'Ended',
};

export const deskClaimEnded = (claim) => {
    if (claim.ended === 'taken_over' && claim.endedBy) return `Taken over by ${claim.endedBy}`;
    if (claim.ended === 'assigned' && claim.endedBy) return `Handset given to someone else by ${claim.endedBy}`;
    if (claim.ended === 'released_by_admin' && claim.endedBy) return `Released by ${claim.endedBy}`;
    return DESK_CLAIM_ENDED[claim.ended] || DESK_CLAIM_ENDED.unknown;
};

export const normaliseDeskHistory = (data) => (Array.isArray(data?.claims) ? data.claims : []).map((c) => ({
    holder: c?.holder || 'Someone',
    from: c?.from || null,
    to: c?.to || null,
    startedBefore: c?.started_before === true,
    ended: DESK_CLAIM_ENDED[c?.ended] ? c.ended : 'unknown',
    endedBy: c?.ended_by || '',
    assignedBy: c?.assigned_by || '',
}));

const HM = (timeZone) => new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone });
const UK_HM = HM('Europe/London');
const WALL_HM = HM('UTC');
// "09:12" in UK time, whoever is looking (staff may be in IST). Two shapes arrive:
// serializer fields use DRF's DATETIME_FORMAT "DD-MM-YYYY HH:MM:SS…Z", which is UK wall-clock
// time despite the Z (GOTCHAS), so its digits are shown as they are; plain responses (the
// history endpoint) send ISO with a real offset, shown in Europe/London.
export const fDeskTime = (value) => {
    if (!value || typeof value !== 'string') return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? '' : UK_HM.format(d);
    }
    const iso = parseApiDate(value);
    return iso ? WALL_HM.format(new Date(iso)) : '';
};

// Active desks that stopped recording, across every connection in useVoiceDesks().byId.
export const stoppedRecordingDesks = (byId) => Object.values(byId || {})
    .flatMap((c) => c?.items || [])
    .filter((d) => d.isActive && d.recording === 'stopped');

export const normaliseDesks = (data) => ({
    items: (Array.isArray(data?.desks) ? data.desks : []).map(normaliseDesk).filter((d) => d.id),
    lastSyncedAt: data?.last_synced_at || null,
    syncError: data?.sync_error || '',
    // Shared Elevate account (and not a superuser): the backend only accepts mapping
    // rows for desks that already took a call for this practice (CU-869fmc1yn).
    limitedToMapped: data?.limited_to_mapped === true,
    // Handsets on this Elevate account that were not sent: counted, never named.
    hiddenCount: Number(data?.hidden_count) || 0,
});

// Go-live checklist: each item is done / not done / unknown (null = no data yet).
// `unregisteredCount` / `desks` undefined = the provider has no lines/desks
// (signed-push), so that row is left out; null = not loaded yet. Desks removed
// from Elevate don't count.

// Team members an admin can put at a desk or give a usual desk (CU-869fmd1vf): enabled
// practice staff, including practice ADMINs (practice managers answer phones; decided
// 2026-10-25). Never SUPPORT or superusers, as in every assignee list
// (utils/filterAssigneeList). Keeps the practice-user id the desk endpoints take.
export const deskMemberOptions = (practiceUsers) => (Array.isArray(practiceUsers) ? practiceUsers : [])
    .filter((pu) => pu?.user && pu.is_enabled && pu.role !== 'SUPPORT' && !pu.user.is_superuser)
    .map((pu) => ({ id: pu.id, name: [pu.user.first_name, pu.user.last_name].filter(Boolean).join(' ') || pu.user.email || `User ${pu.id}`, role: pu.role || '' }))
    .sort((a, b) => a.name.localeCompare(b.name));
